/**
 * AuditeeAccessPanel — compliance-side management of the auditee's portal
 * access, in the audit workspace right rail. Grants are minted, listed, and
 * revoked through /api/audit/audits/:id/auditee-grants; the grant is the only
 * principal the public portal accepts, and its data surface is limited by
 * construction to visible requests and formal correspondence.
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

function lastAccessLabel(g) {
  if (!g.last_access_at) return 'Never opened';
  const days = Math.floor((Date.now() - new Date(g.last_access_at).getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Opened today';
  if (days === 1) return 'Opened yesterday';
  return `Opened ${days}d ago`;
}

function GrantDialog({ auditId, isFdr, onClose, onSent }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const send = async () => {
    setBusy(true); setErr('');
    try {
      const body = { kind: isFdr ? 'fdr_contact' : 'internal_department' };
      if (email.trim()) body.contact_email = email.trim();
      if (name.trim()) body.contact_name = name.trim();
      const res = await fetch(`${API}/api/audit/audits/${auditId}/auditee-grants`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'The invitation could not be sent.');
      onSent(d.grant);
      onClose();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" role="dialog" aria-label="Grant auditee access">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md p-4 space-y-3">
        <div className="text-sm font-semibold text-slate-800">Grant auditee access</div>
        <p className="text-[12px] text-slate-500">
          Emails the contact a secure link to their own audit page. They verify their email with a one-time code,
          then see only the requests you marked visible and what you formally sent them.
        </p>
        <label className="block text-xs text-slate-500">Contact name (optional)
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Jane Rivera" className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
        </label>
        <label className="block text-xs text-slate-500">Contact email
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
        </label>
        {isFdr && <div className="text-[11px] text-slate-400">Leave the email blank to invite the FDR's primary contact on file.</div>}
        {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}
        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600">Cancel</button>
          <button type="button" onClick={send} disabled={busy || (!isFdr && !email.trim())} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AuditeeAccessPanel({ auditId, audit }) {
  const [grants, setGrants] = useState(null);
  const [open, setOpen] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [busyId, setBusyId] = useState('');
  const isFdr = audit?.subject_type === 'fdr';

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/auditee-grants`, { credentials: 'include' }).then((r) => r.json());
      if (r.success) setGrants(r.grants || []);
    } catch {}
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  const revoke = async (g) => {
    if (!window.confirm(`Revoke access for ${g.contact_email}? Their link stops working immediately.`)) return;
    setBusyId(g.id);
    try {
      await fetch(`${API}/api/audit/audits/${auditId}/auditee-grants/${g.id}/revoke`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      await load();
    } catch {}
    setBusyId('');
  };

  const active = (grants || []).filter((g) => !g.revoked_at);
  const revoked = (grants || []).filter((g) => g.revoked_at);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">
          Auditee access{active.length > 0 ? <span className="text-slate-400 font-normal"> · {active.length}</span> : null}
        </div>
        <button type="button" onClick={() => setOpen(true)} className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow">Grant access</button>
      </div>
      <div className="text-[11px] text-slate-400">
        The auditee sees only the requests you mark visible and what you formally send them, never your fieldwork.
      </div>
      {sentTo && (
        <div className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
          Invitation sent to {sentTo}
        </div>
      )}
      {grants !== null && grants.length === 0 && <div className="text-xs text-slate-400">No one has portal access yet.</div>}
      {active.map((g) => (
        <div key={g.id} className="border border-slate-100 rounded-lg px-2.5 py-2">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-slate-700 truncate">{g.contact_name || g.contact_email}</div>
              {g.contact_name && <div className="text-[10px] text-slate-400 truncate">{g.contact_email}</div>}
            </div>
            <button type="button" onClick={() => revoke(g)} disabled={busyId === g.id} className="text-[11px] font-semibold text-slate-400 hover:text-red-600 flex-shrink-0 disabled:opacity-50">Revoke</button>
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            Invited {fmtDate(g.created_at)} · {lastAccessLabel(g)}{g.expires_at ? ` · expires ${fmtDate(g.expires_at)}` : ''}
          </div>
        </div>
      ))}
      {revoked.map((g) => (
        <div key={g.id} className="border border-slate-100 rounded-lg px-2.5 py-2 opacity-60">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1 text-[12px] text-slate-500 truncate">{g.contact_name || g.contact_email}</div>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-100 rounded px-1.5 py-0.5 flex-shrink-0">Revoked</span>
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">Invited {fmtDate(g.created_at)} · {lastAccessLabel(g)}</div>
        </div>
      ))}
      {open && <GrantDialog auditId={auditId} isFdr={isFdr} onClose={() => setOpen(false)} onSent={(g) => { setSentTo(g?.contact_email || ''); load(); }} />}
    </div>
  );
}
